import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readFormats } from "@/office/excel/formate";
import { nutztEineFunktion } from "@/office/excel/formeln";
import { filledSheets, isEmpty, formulaCells, readWorkbook } from "@/office/excel/workbook";
import { KEIN_INHALT, nichtPruefbar } from "../schnittstelle";

export const lektionDuplikate: Lesson = {
  id: "duplikate",
  title: "Duplikate finden und entfernen",
  summary: "Doppelte Zeilen sind der Normalfall in gewachsenen Listen – und gefährlich zu löschen.",
  durationMinutes: 10,
  learningGoals: [
    "Doppelte Werte sichtbar machen, bevor man sie anfasst",
    "„Duplikate entfernen“ einsetzen und seine Tücken kennen",
    "Entscheiden, was in einer Liste überhaupt ein Duplikat ist",
  ],
  steps: [
    {
      id: "was-ist-ein-duplikat",
      title: "Zuerst die Frage, was doppelt heißt",
      explanation: [
        "Bevor irgendetwas gelöscht wird, gehört eine Entscheidung getroffen: Wann " +
          "sind zwei Zeilen dasselbe? Zwei Bestellungen desselben Kunden am selben Tag " +
          "über denselben Artikel können ein Fehler sein – oder zwei echte " +
          "Bestellungen. Eine Kundennummer, die zweimal vorkommt, ist in einer " +
          "Kundenliste ein Fehler und in einer Bestellliste völlig normal.",
        "Excel beantwortet diese Frage nicht. Es vergleicht genau die Spalten, die man " +
          "ihm nennt. Wer beim Entfernen von Duplikaten alle Spalten anhakt, findet " +
          "nur exakt identische Zeilen; wer nur die Kundennummer anhakt, löscht alles " +
          "bis auf die erste Zeile je Kunde.",
        "Zwei Werte sind für Excel dabei gleich, wenn sie nach dem Zahlenformat gleich " +
          "aussehen – nicht nach dem gespeicherten Wert. 1,004 und 1,0049 gelten bei " +
          "zwei angezeigten Nachkommastellen als Duplikat. Groß- und Kleinschreibung " +
          "ignoriert Excel, ein Leerzeichen am Ende dagegen nicht: „Meier“ und " +
          "„Meier “ sind zwei verschiedene Werte.",
      ],
      keyPoints: [
        "Erst festlegen, welche Spalten über „doppelt“ entscheiden.",
        "Excel vergleicht die Anzeige, nicht den gespeicherten Wert.",
        "Groß- und Kleinschreibung egal, ein Leerzeichen am Ende nicht.",
      ],
    },
    {
      id: "sichtbar-machen",
      title: "Erst sichtbar machen, dann handeln",
      explanation: [
        "Der ungefährliche Weg führt über das Anzeigen. Zwei Werkzeuge stehen dafür " +
          "bereit, und beide löschen nichts.",
        "Die bedingte Formatierung unter „Start“ → „Bedingte Formatierung“ → " +
          "„Regeln zum Hervorheben von Zellen“ → „Doppelte Werte“ färbt jede Zelle " +
          "ein, deren Wert in der Markierung mehr als einmal vorkommt. Ein Klick, " +
          "sofort sichtbar. Der Haken: Sie arbeitet spaltenweise – über mehrere " +
          "Spalten hinweg gleiche Zeilen findet sie nicht.",
        "ZÄHLENWENN ist die genauere Variante: Eine Hilfsspalte mit " +
          "=ZÄHLENWENN(A:A;A2) zeigt für jede Zeile, wie oft ihr Wert insgesamt " +
          "vorkommt. Alles über 1 ist ein Duplikat, und die Spalte lässt sich " +
          "sortieren und filtern. Sollen mehrere Spalten zusammen entscheiden, hilft " +
          "eine Hilfsspalte, die sie erst verkettet: =A2&\"|\"&B2, und darauf dann das " +
          "ZÄHLENWENN.",
        "Der Trennstrich in der Verkettung ist kein Schmuck. Ohne ihn ergäben „AB“ " +
          "plus „C“ und „A“ plus „BC“ denselben Text – und zwei verschiedene Zeilen " +
          "sähen wie ein Duplikat aus.",
      ],
      keyPoints: [
        "Bedingte Formatierung → Doppelte Werte färbt sofort ein, spaltenweise.",
        "=ZÄHLENWENN(A:A;A2) zählt, wie oft ein Wert vorkommt.",
        "Für mehrere Spalten erst mit einem Trennzeichen verketten.",
      ],
      task: {
        instruction:
          "Mache mögliche Duplikate in deiner Liste sichtbar – entweder mit der " +
          "bedingten Formatierung „Doppelte Werte“ oder mit einer Hilfsspalte, die " +
          "ZÄHLENWENN benutzt.",
        hint: "Spalte markieren, dann Start → Bedingte Formatierung → Regeln zum Hervorheben von Zellen → Doppelte Werte.",
        check: async () => {
          const mappe = await readWorkbook();
          const formate = await readFormats();

          const mitZaehlenwenn = mappe.features.cells
            ? formulaCells(mappe).filter((zelle) =>
                nutztEineFunktion(zelle.formula, ["zaehlenwenn", "zaehlenwenns"]),
              )
            : [];

          if (mitZaehlenwenn.length > 0) {
            const erste = mitZaehlenwenn[0];
            return pass(
              `In ${erste.address} zählt „${erste.formula}“ das Vorkommen der Werte. ` +
                "Sortiere oder filtere diese Spalte absteigend – alles über 1 ist ein Duplikat.",
            );
          }

          if (formate.conditionalFormats === null && !mappe.features.cells) {
            return nichtPruefbar(
              "Bedingte Formatierungen und Zellformeln",
              "1.6",
              "Sieh nach, ob doppelte Werte in deiner Liste farbig hervorgehoben sind.",
              true,
            );
          }

          if (formate.conditionalFormats !== null) {
            // "Doppelte Werte" legt Excel als Regel vom Typ "Custom" an - der
            // Regeltyp allein reicht deshalb nicht, es zählt jede Regel.
            const regeln = formate.conditionalFormats;
            if (regeln.length > 0) {
              const erste = regeln[0];
              return pass(
                `Auf „${erste.sheetName}“ liegt über ${erste.address ?? "einen Bereich"} eine bedingte ` +
                  `Formatierung vom Typ „${erste.type}“. Prüfe kurz, ob sie wirklich doppelte Werte ` +
                  "hervorhebt – der genaue Regelinhalt lässt sich hier nicht in jedem Fall auslesen.",
              );
            }
          }

          if (mappe.features.cells && filledSheets(mappe).length === 0) {
            return fail(KEIN_INHALT, "Trage zuerst deine Liste ein.");
          }

          // Wer den Weg über die bedingte Formatierung gewählt hat, diese
          // Excel-Version sie aber nicht ausliest, würde hier sonst ohne
          // Erklärung scheitern. Der zweite Weg bleibt in jedem Fall prüfbar.
          const regelnUnlesbar = formate.conditionalFormats === null;
          return fail(
            regelnUnlesbar
              ? "Keine ZÄHLENWENN-Hilfsspalte gefunden. Ob eine bedingte Formatierung vorliegt, kann " +
                "diese Excel-Version nicht melden – dafür bräuchte es den Anforderungssatz ExcelApi 1.6."
              : "Weder eine bedingte Formatierung noch eine ZÄHLENWENN-Hilfsspalte gefunden.",
            regelnUnlesbar
              ? "Nimm für diese Aufgabe den zweiten Weg: Schreibe neben deine Liste =ZÄHLENWENN(A:A;A2) " +
                "und passe die Spalte an. Der lässt sich hier prüfen."
              : "Markiere eine Spalte und wähle Start → Bedingte Formatierung → Regeln zum Hervorheben " +
                "von Zellen → Doppelte Werte. Oder schreibe daneben =ZÄHLENWENN(A:A;A2).",
          );
        },
      },
    },
    {
      id: "entfernen",
      title: "Duplikate entfernen – mit Vorsicht",
      explanation: [
        "Unter „Daten“ → „Duplikate entfernen“ steckt der große Hammer. Der Dialog " +
          "zeigt alle Spalten mit Häkchen; nur die angehakten entscheiden über " +
          "„doppelt“. Excel behält jeweils die erste gefundene Zeile und löscht alle " +
          "weiteren – ohne zu fragen, welche das war.",
        "Das ist endgültig, und es gibt keine Vorschau. Was gelöscht wurde, erfährt " +
          "man erst hinterher aus einer Meldung, die die Anzahl nennt. Wer eine " +
          "vollständige Liste braucht, sortiert vorher so, dass die Zeile, die bleiben " +
          "soll, oben steht – etwa nach Datum absteigend, damit der neueste Stand " +
          "gewinnt.",
        "Die Vorsichtsmaßnahme, die sich immer lohnt: das Blatt vorher kopieren. " +
          "Rechtsklick auf das Register, „Verschieben oder kopieren“, Häkchen bei " +
          "„Kopie erstellen“. Dann liegt das Original unangetastet daneben, und die " +
          "Bereinigung lässt sich nachvollziehen.",
        "Für eine Tabelle im Sinne von Modul 5 gilt dasselbe, mit einem angenehmen " +
          "Unterschied: Weil die Tabelle ihre Grenzen kennt, kann „Duplikate entfernen“ " +
          "hier nicht versehentlich den falschen Bereich erwischen.",
      ],
      keyPoints: [
        "Daten → Duplikate entfernen löscht endgültig, ohne Vorschau.",
        "Nur die angehakten Spalten entscheiden über „doppelt“.",
        "Excel behält die erste Zeile – vorher passend sortieren.",
        "Blatt vorher kopieren: Rechtsklick aufs Register → Kopie erstellen.",
      ],
      task: {
        instruction:
          "Sorge dafür, dass in deiner Liste keine vollständig doppelten Zeilen mehr " +
          "stehen.",
        hint: "Erst prüfen, welche Zeilen doppelt sind, dann Daten → Duplikate entfernen. Vorher das Blatt kopieren.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Der Inhalt der Zellen",
              "1.4",
              "Sortiere deine Liste und sieh nach, ob zwei aufeinanderfolgende Zeilen völlig gleich sind.",
            );
          }

          const blaetter = filledSheets(mappe);
          if (blaetter.length === 0) {
            return fail(KEIN_INHALT, "Trage zuerst deine Liste ein.");
          }

          for (const blatt of blaetter) {
            const gefuellt = blatt.cells.filter((zelle) => !isEmpty(zelle));
            if (gefuellt.length === 0) continue;

            const ersteZeile = Math.min(...gefuellt.map((zelle) => zelle.row));
            const zeilen = new Map<number, string[]>();
            for (const zelle of gefuellt) {
              if (zelle.row === ersteZeile) continue; // Kopfzeile
              const eintrag = zeilen.get(zelle.row) ?? [];
              eintrag.push(`${zelle.column}:${zelle.text}`);
              zeilen.set(zelle.row, eintrag);
            }

            const gesehen = new Map<string, number>();
            for (const [zeile, werte] of [...zeilen.entries()].sort((a, b) => a[0] - b[0])) {
              if (werte.length < 2) continue; // eine einzelne Zelle ist keine Datenzeile
              const schluessel = werte.sort().join("|");
              const vorher = gesehen.get(schluessel);
              if (vorher !== undefined) {
                return fail(
                  `Auf „${blatt.name}“ ist Zeile ${zeile} identisch mit Zeile ${vorher} – ` +
                    "in allen gefüllten Spalten.",
                  "Kopiere das Blatt zur Sicherheit, setze dann den Cursor in die Daten und wähle " +
                    "Daten → Duplikate entfernen. Prüfe im Dialog, welche Spalten angehakt sind.",
                );
              }
              gesehen.set(schluessel, zeile);
            }
          }

          return pass("Keine vollständig doppelte Zeile gefunden – jede Zeile deiner Liste ist einmalig.");
        },
      },
    },
  ],
};
